import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'
import { Note } from '../../shared/types'

export interface BatchDeleteItem {
  id: string
  title: string
  hasChildren: boolean
  childrenCount: number
  level: number
}

export type BatchDeleteStatus = 'idle' | 'confirming' | 'processing' | 'completed' | 'cancelled'

export interface BatchDeleteState {
  isOpen: boolean
  items: BatchDeleteItem[]
  currentIndex: number
  status: BatchDeleteStatus
  results: {
    deleted: string[]
    skipped: string[]
    movedToRoot: string[]
  }
}

export interface BatchDeleteContextValue extends BatchDeleteState {
  startBatchDelete: (noteId: string, notes: Note[]) => void
  startBatchDeleteMany: (noteIds: string[], notes: Note[]) => void
  confirmDelete: () => void
  skipCurrent: () => void
  moveToRoot: () => void
  markDeleted: (noteId: string) => void
  cancelBatch: () => void
  processNext: () => void
  getCurrentItem: () => BatchDeleteItem | null
  getProgress: () => { current: number; total: number; percentage: number }
}

type BatchDeleteAction =
  | { type: 'START_BATCH'; payload: { items: BatchDeleteItem[] } }
  | { type: 'CONFIRM_DELETE' }
  | { type: 'SKIP_CURRENT' }
  | { type: 'MOVE_TO_ROOT' }
  | { type: 'CANCEL_BATCH' }
  | { type: 'PROCESS_NEXT' }
  | { type: 'MARK_DELETED'; payload: { noteId: string } }
  | { type: 'MARK_SKIPPED'; payload: { noteId: string } }
  | { type: 'MARK_MOVED_TO_ROOT'; payload: { noteId: string } }
  | { type: 'COMPLETE_BATCH' }
  | { type: 'RESET_STATE' }

const initialState: BatchDeleteState = {
  isOpen: false,
  items: [],
  currentIndex: 0,
  status: 'idle',
  results: {
    deleted: [],
    skipped: [],
    movedToRoot: [],
  },
}

function batchDeleteReducer(state: BatchDeleteState, action: BatchDeleteAction): BatchDeleteState {
  switch (action.type) {
    case 'START_BATCH':
      return {
        ...initialState,
        isOpen: true,
        items: action.payload.items,
        status: 'confirming',
      }

    case 'CONFIRM_DELETE':
      return {
        ...state,
        status: 'processing',
      }

    case 'SKIP_CURRENT': {
      // 跳过功能已改为移到根目录，使用 MOVE_TO_ROOT 替代
      return state
    }

    case 'MOVE_TO_ROOT': {
      const currentItem = state.items[state.currentIndex]
      if (!currentItem) return state

      const newMovedToRoot = [...state.results.movedToRoot, currentItem.id]
      const nextIndex = state.currentIndex + 1
      const isComplete = nextIndex >= state.items.length

      return {
        ...state,
        currentIndex: nextIndex,
        status: isComplete ? 'completed' : state.status,
        results: {
          ...state.results,
          movedToRoot: newMovedToRoot,
        },
      }
    }

    case 'CANCEL_BATCH':
      return {
        ...state,
        status: 'cancelled',
        isOpen: false,
      }

    case 'PROCESS_NEXT': {
      const nextIndex = state.currentIndex + 1
      const isComplete = nextIndex >= state.items.length

      return {
        ...state,
        currentIndex: nextIndex,
        status: isComplete ? 'completed' : state.status,
      }
    }

    case 'MARK_DELETED': {
      const newDeleted = [...state.results.deleted, action.payload.noteId]
      return {
        ...state,
        results: {
          ...state.results,
          deleted: newDeleted,
        },
      }
    }

    case 'MARK_SKIPPED': {
      const newSkipped = [...state.results.skipped, action.payload.noteId]
      return {
        ...state,
        results: {
          ...state.results,
          skipped: newSkipped,
        },
      }
    }

    case 'MARK_MOVED_TO_ROOT': {
      const newMovedToRoot = [...state.results.movedToRoot, action.payload.noteId]
      return {
        ...state,
        results: {
          ...state.results,
          movedToRoot: newMovedToRoot,
        },
      }
    }

    case 'COMPLETE_BATCH':
      return {
        ...state,
        status: 'completed',
      }

    case 'RESET_STATE':
      return initialState

    default:
      return state
  }
}

function collectChildrenRecursive(noteId: string, notes: Note[], level: number = 0, visited: Set<string> = new Set()): BatchDeleteItem[] {
  // 防止循环引用
  if (visited.has(noteId)) return []
  visited.add(noteId)

  const note = notes.find(n => n.id === noteId)
  if (!note) return []

  // 通过 parentId 查找子笔记
  const children = notes.filter(n => n.parentId === noteId)
  const childrenCount = children.length

  const item: BatchDeleteItem = {
    id: note.id,
    title: note.title,
    hasChildren: childrenCount > 0,
    childrenCount,
    level,
  }

  // 先收集所有子笔记（深层优先）
  const result: BatchDeleteItem[] = []

  for (const child of children) {
    const childItems = collectChildrenRecursive(child.id, notes, level + 1, visited)
    result.push(...childItems)
  }

  // 最后添加父笔记（确保子笔记先被删除）
  result.push(item)

  return result
}

function collectMultipleRoots(noteIds: string[], notes: Note[]): BatchDeleteItem[] {
  // 跨根共享 visited，天然去重；collectChildrenRecursive 内部"先子后父"顺序保持不变
  const visited = new Set<string>()
  const result: BatchDeleteItem[] = []
  for (const id of noteIds) {
    result.push(...collectChildrenRecursive(id, notes, 0, visited))
  }
  return result
}

const BatchDeleteContext = createContext<BatchDeleteContextValue | undefined>(undefined)

export const BatchDeleteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(batchDeleteReducer, initialState)

  const startBatchDeleteMany = useCallback((noteIds: string[], notes: Note[]) => {
    const items = collectMultipleRoots(noteIds, notes)
    dispatch({ type: 'START_BATCH', payload: { items } })
  }, [])

  const startBatchDelete = useCallback((noteId: string, notes: Note[]) => {
    startBatchDeleteMany([noteId], notes)
  }, [startBatchDeleteMany])

  const confirmDelete = useCallback(() => {
    dispatch({ type: 'CONFIRM_DELETE' })
  }, [])

  const skipCurrent = useCallback(() => {
    dispatch({ type: 'SKIP_CURRENT' })
  }, [])

  const moveToRoot = useCallback(() => {
    dispatch({ type: 'MOVE_TO_ROOT' })
  }, [])

  const markDeleted = useCallback((noteId: string) => {
    dispatch({ type: 'MARK_DELETED', payload: { noteId } })
  }, [])

  const cancelBatch = useCallback(() => {
    dispatch({ type: 'CANCEL_BATCH' })
  }, [])

  const processNext = useCallback(() => {
    dispatch({ type: 'PROCESS_NEXT' })
  }, [])

  const getCurrentItem = useCallback((): BatchDeleteItem | null => {
    if (state.currentIndex >= state.items.length) return null
    return state.items[state.currentIndex]
  }, [state.currentIndex, state.items])

  const getProgress = useCallback(() => {
    const total = state.items.length
    const current = Math.min(state.currentIndex + 1, total)
    const percentage = total > 0 ? Math.round((state.currentIndex / total) * 100) : 0
    return { current, total, percentage }
  }, [state.currentIndex, state.items.length])

  const value: BatchDeleteContextValue = {
    ...state,
    startBatchDelete,
    startBatchDeleteMany,
    confirmDelete,
    skipCurrent,
    moveToRoot,
    markDeleted,
    cancelBatch,
    processNext,
    getCurrentItem,
    getProgress,
  }

  return (
    <BatchDeleteContext.Provider value={value}>
      {children}
    </BatchDeleteContext.Provider>
  )
}

export const useBatchDelete = (): BatchDeleteContextValue => {
  const context = useContext(BatchDeleteContext)
  if (context === undefined) {
    throw new Error('useBatchDelete must be used within a BatchDeleteProvider')
  }
  return context
}

export default BatchDeleteContext
